import { DOCUMENT } from '@angular/common';
import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  makeStateKey,
  REQUEST_CONTEXT,
  signal,
  Signal,
  TransferState,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Meta } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { catchError, defer, finalize, firstValueFrom, map, Observable, of, Subject, takeUntil } from 'rxjs';
import { ConfigModel, GsStructComponent, IServerRequestContext, PageComponentType } from '../../models';
import { DataProvider } from '../../providers/basic/data.service';
import { ConfigProvider } from '../../providers/config/config.service';
import { EnvironmentService } from '../environment/environment.service';
import { FileService } from '../file/file.service';
import { LocalStorageService } from '../localstorage.service';
import { SystemService } from '../system.service';
import { ToastService } from '../toast.service';
import { convertFontAwesomeIcons } from '../../utils/fontawesome-icon.util';
import { CONFIG_VERSION, isApplicableConfig } from './config.constants';
import { DEFAULT_CONFIG } from './default.config';
import { CONFIG_COLORS_FORM } from './forms/config-colors.form';
import { generateTagStylesFormGroup } from './forms/config-tag-styles.form';
import { buildThemeCss, THEME_STYLE_ID } from './theme-css';

export { CONFIG_VERSION, isApplicableConfig };

/** TransferState key of the config the server rendered with */
const SERVER_CONFIG_STATE_KEY = makeStateKey<ConfigModel>('config');

@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  // Services
  private readonly configProvider = inject(ConfigProvider);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly envService = inject(EnvironmentService);
  private readonly fileService = inject(FileService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly meta = inject(Meta);
  /** Set by server.ts while rendering on the server, null in the browser */
  private readonly requestContext = inject(REQUEST_CONTEXT, { optional: true }) as IServerRequestContext | null;
  private readonly router = inject(Router);
  private readonly systemService = inject(SystemService);
  private readonly toastService = inject(ToastService);
  private readonly transferState = inject(TransferState);
  private readonly translateService = inject(TranslateService);

  // Properties & Signals
  public config: WritableSignal<ConfigModel | undefined> = signal<ConfigModel | undefined>(undefined);
  public formGroup?: FormGroup;
  /** Stable fingerprint of the last backend-synced config; baseline for unsaved-changes detection */
  private readonly savedConfigFingerprint: WritableSignal<string> = signal('');
  /** Whether the current config differs from the last backend-synced state (derived — never set directly) */
  public readonly hasUnsavedChanges: Signal<boolean> = computed(
    () => this.configFingerprint(this.config()) !== this.savedConfigFingerprint()
  );
  public backendUpdatedAt: WritableSignal<Date | undefined> = signal(undefined);
  /** Config version reported by the backend (may differ from CONFIG_VERSION) */
  public backendConfigVersion: WritableSignal<number | undefined> = signal(undefined);
  public isLoadingFromBackend: WritableSignal<boolean> = signal(false);
  /** Whether a backend save is currently in-flight */
  public isSaving: WritableSignal<boolean> = signal(false);
  private destroySubscriptions = new Subject<void>();

  /** LocalStorage key for tracking last backend load time */
  private readonly LAST_LOAD_KEY = 'config_last_backend_load';

  constructor() {
    // The browser starts with the config the server rendered with, so hydration finds the same header and footer
    const serverConfig = this.transferState.get(SERVER_CONFIG_STATE_KEY, null);
    if (serverConfig) this.config.set(serverConfig);
    if (!this.systemService.browser) return;

    // Prevent browser close while a save is in-flight
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (this.isSaving()) {
        event.preventDefault();
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    this.destroyRef.onDestroy(() => window.removeEventListener('beforeunload', onBeforeUnload));
  }

  // Methods
  /**
   * Initializes the config service. On the server it takes the config from the request context.
   * @param force Should the config be loaded even if it is already loaded
   * @param shouldRouteToConfigOnFail Whether a development build opens the config page when no config exists
   */
  async initialize(force?: boolean, shouldRouteToConfigOnFail: boolean = false) {
    if (!this.systemService.browser) {
      this.initServerConfig();
      return;
    }
    SystemService.debug('[ConfigService] Initializing... Is browser', '');
    await this.loadConfig(force, shouldRouteToConfigOnFail);
    this.buildForm();
  }

  /**
   * Loads the config from localstorage or backend
   * @param force Should the config be loaded even if it is already loaded
   */
  private async loadConfig(force?: boolean, shouldRouteToConfigOnFail: boolean = false): Promise<void> {
    if (this.adoptServerConfig()) return;
    // The cached config can still carry the FontAwesome icons of an older version
    const config: ConfigModel = convertFontAwesomeIcons(this.localStorageService.get('config'));
    if (config) {
      this.config.update(() => config);
      this.systemService.log('[ConfigService] Config loaded from localstorage', this.config());
    }

    const shouldReload = this.shouldReloadConfig(config, force);

    if (shouldReload) {
      this.systemService.log('[ConfigService] Config not found on localstorage or was too old, loading from backend...', force);
      this.isLoadingFromBackend.set(true);
      try {
        await this.loadBackendConfig(force);
        if (!this.config()) {
          this.initConfig();
          if (this.systemService.isDevelopment && shouldRouteToConfigOnFail) this.router.navigateByUrl('/config');
          this.systemService.log('[ConfigService] No config found, using default config', this.config());
        }
      } finally {
        this.isLoadingFromBackend.set(false);
      }
    }
    // Establish the saved baseline: whatever config we hold now is considered in sync with the backend
    this.savedConfigFingerprint.set(this.configFingerprint(this.config()));
    this.applyTheme();
  }

  /**
   * Sets the config the server renders with from the request context, hands it to the browser
   * through the TransferState and renders its theme and site verification into the page. server.ts
   * loads and caches it, so no request is made here.
   */
  private initServerConfig(): void {
    const data = this.requestContext?.config;
    if (!isApplicableConfig(data)) return;
    // The context object is server.ts' cache entry, shared by all requests of the channel
    const config = this.prepareBackendConfig(structuredClone(data));
    this.config.set(config);
    this.transferState.set(SERVER_CONFIG_STATE_KEY, config);
    this.applyTheme();
    this.applySiteVerification(config);
  }

  /**
   * Writes the `google-site-verification` meta tag of the config into the page head, so the Search Console
   * ownership check finds it without running the bundle. Search Console offers the whole `<meta>` tag for
   * copy & paste, so a pasted tag is accepted as well as the bare token.
   * @param config The config of the server render.
   */
  private applySiteVerification(config: ConfigModel): void {
    const value = config.seo?.googleSiteVerification?.trim() ?? '';
    const token = (/content=["']([^"']+)["']/i.exec(value)?.[1] ?? value).trim();
    if (token) this.meta.updateTag({ name: 'google-site-verification', content: token });
  }

  /**
   * Takes over the config the server rendered with: stores it as the fresh localStorage config, so the
   * stale localStorage copy does not replace it and the next backend reload waits for the polling interval.
   * @returns True when a server config was taken over
   */
  private adoptServerConfig(): boolean {
    const serverConfig = this.transferState.get(SERVER_CONFIG_STATE_KEY, null);
    if (!serverConfig) return false;
    this.transferState.remove(SERVER_CONFIG_STATE_KEY);
    this.config.set(serverConfig);
    this.saveConfig();
    this.localStorageService.set(this.LAST_LOAD_KEY, Date.now());
    this.savedConfigFingerprint.set(this.configFingerprint(serverConfig));
    this.applyTheme();
    return true;
  }

  /**
   * Prepares a config loaded from the backend for use.
   * @param data The config as the backend delivered it, mapped to camelCase
   * @returns The same config with capitalized component keys and the app key of the environment
   */
  private prepareBackendConfig(data: ConfigModel): ConfigModel {
    // Configs saved before the Phosphor icons carry FontAwesome names and styles
    convertFontAwesomeIcons(data);
    // Capitalize component keys (e.g. 'button' -> 'Button') since mapCasesReverse lowercases them
    if (data.components) {
      data.components = this.capitalizeComponentKeys(data.components);
      this.validateComponentTypes(data.components);
    }
    data.appKey = this.envService.env.APP_KEY;
    return data;
  }

  /**
   * Determines if the config should be reloaded from the backend
   * @param config The current config from localStorage
   * @param force Whether to force reload
   * @returns true if the config should be reloaded
   */
  private shouldReloadConfig(config: ConfigModel | undefined, force?: boolean): boolean {
    // Always reload if force is true or config is undefined
    if (force || !config) {
      return true;
    }

    const pollingInterval = config.pollingInterval ?? 5;
    // If pollingInterval is 0, always reload
    if (pollingInterval === 0) {
      this.systemService.log('[ConfigService] Polling interval is 0, always reload');
      return true;
    }
    const pollingIntervalMs = pollingInterval * 60 * 1000;

    const rawLastLoadTime = this.localStorageService.get(this.LAST_LOAD_KEY);
    let lastLoadTime: number | null = null;
    if (typeof rawLastLoadTime === 'number' && Number.isFinite(rawLastLoadTime) && rawLastLoadTime > 0) {
      lastLoadTime = rawLastLoadTime;
    } else if (typeof rawLastLoadTime === 'string') {
      const parsed = Number(rawLastLoadTime);
      if (Number.isFinite(parsed) && parsed > 0) {
        lastLoadTime = parsed;
      }
    }
    // Interval throttle applies to ALL reload reasons (incl. a bumped CONFIG_VERSION):
    // never bypass it, otherwise a code/backend version gap would trigger a reload on
    // every initialize() and hammer the backend. A version-driven refresh is handled by
    // the regular polling below - loadBackendConfig() only applies compatible versions.
    if (lastLoadTime !== null) {
      const timeSinceLastLoad = Date.now() - lastLoadTime;
      if (timeSinceLastLoad < pollingIntervalMs) {
        this.systemService.log(
          `[ConfigService] Skipping backend reload - last load was ${Math.round(timeSinceLastLoad / 1000)}s ago (interval: ${pollingIntervalMs / 1000}s)`
        );
        return false;
      }
    }

    // If we reach here, polling interval has elapsed since last load, so reload
    return true;
  }

  /**
   * Saves the config to the localstorage
   */

  public saveConfig() {
    const currentConfig = this.config();
    this.systemService.log('[ConfigService] Config saving in localstorage...', currentConfig);
    if (currentConfig) this.localStorageService.set('config', currentConfig);
  }

  /**
   * Sets the component defaults in the config signal and in the `components` control of the form group.
   * Both have to stay in sync: every form change writes the whole form value back into the config,
   * so a stale `components` control would overwrite the new defaults on the next edit of another setting.
   * @param components the new component defaults
   */
  public setComponents(components: Record<PageComponentType, GsStructComponent<any>>): void {
    this.config.update((cfg) => {
      // No config yet (first save on a fresh project): start from the shipped defaults
      const base = cfg ?? (JSON.parse(JSON.stringify(DEFAULT_CONFIG)) as ConfigModel);
      return { ...base, components };
    });
    this.formGroup?.get('components')?.setValue(components, { emitEvent: false });
  }

  /**
   * Deletes the config from the localstorage
   */
  public deleteConfig() {
    this.localStorageService.remove('config');
    this.localStorageService.remove(this.LAST_LOAD_KEY);
  }

  /**
   * Initializes the config with the default values
   */
  private initConfig() {
    this.config.update(() => {
      const currentDate = new Date().toISOString();
      return { ...DEFAULT_CONFIG, updatedAt: currentDate, appKey: this.envService.env?.APP_KEY };
    });
    SystemService.debug('Config initialized', this.config());
  }

  /**
   * Builds the form group for the templates settings. Also defining the actions on change.
   */
  private buildForm() {
    const currentConfig = this.config();
    this.destroySubscriptions.next();
    const colorsForm = CONFIG_COLORS_FORM;
    const tagStylesForm = generateTagStylesFormGroup();
    if (this.formGroup) {
      this.formGroup.patchValue({
        footer: currentConfig?.footer ?? DEFAULT_CONFIG.footer ?? {},
        header: currentConfig?.header ?? DEFAULT_CONFIG.header ?? {},
        components: currentConfig?.components ?? DEFAULT_CONFIG.components ?? {},
        uiElements: currentConfig?.uiElements ?? DEFAULT_CONFIG.uiElements ?? {},
        updatedAt: currentConfig?.updatedAt ?? DEFAULT_CONFIG.updatedAt ?? new Date().toISOString(),
        appKey: currentConfig?.appKey ?? this.envService.env.APP_KEY ?? '',
        version: currentConfig?.version ?? CONFIG_VERSION ?? '',
        pollingInterval: currentConfig?.pollingInterval ?? DEFAULT_CONFIG.pollingInterval,
        initPollingInterval: currentConfig?.initPollingInterval ?? DEFAULT_CONFIG.initPollingInterval,
        seo: currentConfig?.seo ?? DEFAULT_CONFIG.seo ?? {}
      });
      this.formGroup.get('colors')?.patchValue(currentConfig?.colors ?? colorsForm.value ?? {});
      this.formGroup.get('tagStyles')?.patchValue(currentConfig?.tagStyles ?? tagStylesForm.value ?? {});
      this.formGroup.updateValueAndValidity();
    } else {
      if (currentConfig?.colors) colorsForm.patchValue(currentConfig.colors);
      if (currentConfig?.tagStyles) tagStylesForm.patchValue(currentConfig.tagStyles);
      this.formGroup = this.formBuilder.group({
        footer: this.formBuilder.control(currentConfig?.footer ?? DEFAULT_CONFIG.footer ?? {}),
        header: this.formBuilder.control(currentConfig?.header ?? DEFAULT_CONFIG.header ?? {}),
        colors: colorsForm,
        tagStyles: tagStylesForm,
        components: this.formBuilder.control(currentConfig?.components ?? DEFAULT_CONFIG.components ?? {}),
        uiElements: this.formBuilder.control(currentConfig?.uiElements ?? DEFAULT_CONFIG.uiElements ?? {}),
        updatedAt: this.formBuilder.control(currentConfig?.updatedAt ?? DEFAULT_CONFIG.updatedAt),
        appKey: this.formBuilder.control(currentConfig?.appKey ?? this.envService.env.APP_KEY),
        version: this.formBuilder.control(CONFIG_VERSION),
        pollingInterval: this.formBuilder.control(currentConfig?.pollingInterval ?? DEFAULT_CONFIG.pollingInterval),
        initPollingInterval: this.formBuilder.control(currentConfig?.initPollingInterval ?? DEFAULT_CONFIG.initPollingInterval),
        seo: this.formBuilder.group({
          googleSiteVerification: this.formBuilder.control(
            currentConfig?.seo?.googleSiteVerification ?? DEFAULT_CONFIG.seo?.googleSiteVerification ?? ''
          )
        })
      });
    }

    this.formGroup.valueChanges.pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.destroySubscriptions)).subscribe((value) => {
      const currentConfigVal = this.config();
      if (!currentConfigVal) return;
      this.formGroup?.get('updatedAt')?.setValue(new Date().toISOString(), {
        emitEvent: false
      });
      this.config.update((cfg) => {
        return { ...cfg, ...value };
      });
    });
  }

  /**
   * Writes the theme of the current config into the `<style id="gs-theme">` element. The server
   * render creates that element; the client takes it over and keeps it in sync.
   */
  applyTheme(): void {
    let style = this.document.getElementById(THEME_STYLE_ID);
    if (!style) {
      style = this.document.createElement('style');
      style.id = THEME_STYLE_ID;
      this.document.head.appendChild(style);
    }
    style.textContent = buildThemeCss(this.config());
  }

  /**
   * Loads the config from the backend
   * @param force Should the config be loaded even if it is already loaded
   * @returns The loaded config or null if no newer config was found
   */
  async loadBackendConfig(force?: boolean): Promise<ConfigModel | undefined> {
    const currentConfig = this.config();
    const args = !force && currentConfig?.updatedAt ? { timestamp: currentConfig?.updatedAt } : {};
    let data;
    try {
      data = await firstValueFrom(this.configProvider.whereOne(args));
      // Timestamp the backend round-trip so shouldReloadConfig() can skip reloads within the polling interval
      this.localStorageService.set(this.LAST_LOAD_KEY, Date.now());
    } catch (error) {
      this.systemService.log('[ConfigService] Error loading config from backend', error);
      return undefined;
    }
    if (!data) return undefined;
    // Check if the loaded config is newer than the current one
    const dataDate = data.updatedAt ? new Date(data.updatedAt) : undefined;
    this.backendUpdatedAt.set(dataDate);
    this.backendConfigVersion.set(data.version);
    const currentDate = currentConfig?.updatedAt ? new Date(currentConfig.updatedAt) : undefined;
    const dataValid = dataDate && !isNaN(dataDate.getTime());
    const currentValid = currentDate && !isNaN(currentDate.getTime());
    if (!dataValid || !currentValid || dataDate.getTime() > currentDate.getTime()) {
      if (data.version <= CONFIG_VERSION) {
        if (typeof data === 'string' || data instanceof String) {
          console.error('[ConfigService] Loaded config is not an object, using default!', data);
          this.config.update(() => DEFAULT_CONFIG);
        } else {
          this.prepareBackendConfig(data);
          // Warn if backend returns config without valid updatedAt - this is a data quality issue
          // We don't fake the timestamp as it would mask stale data and affect reload decisions
          if (!data.updatedAt || isNaN(new Date(data.updatedAt).getTime())) {
            this.systemService.log(
              '[ConfigService] Warning: Backend returned config without valid updatedAt timestamp. Config will be treated as potentially stale on future reloads.'
            );
          }
          console.log('[ConfigService] Loaded config from backend', data);
          this.config.update(() => data);
        }
        this.saveConfig();
        // Sync an existing form with the loaded config, otherwise its stale value overwrites the config on the next edit
        if (this.formGroup) this.buildForm();
      } else {
        this.systemService.log(
          `[ConfigService] Codebase Config-Version [${CONFIG_VERSION}] is higher than backend Config-Version [${data.version}]. Skipping update of config to prevent errors!`,
          {
            localConfig: this.config() ?? DEFAULT_CONFIG,
            backendConfig: data
          }
        );
      }
    }
    return this.config();
  }

  /**
   * Saves the current config to the backend. Cold observable — the request fires on subscribe.
   * Toggles `isSaving` for the duration and refreshes the saved baseline on success.
   * The save button is disabled while `isSaving`, so concurrent saves cannot occur.
   * @returns Observable that emits true on success, false on failure (errors are mapped to false)
   */
  saveBackendConfig(): Observable<boolean> {
    return defer(() => {
      const currentConfig = this.config();
      if (!currentConfig) return of(false);
      currentConfig.updatedAt = new Date().toISOString();
      this.config.update(() => currentConfig);
      // Fingerprint the exact snapshot being persisted, captured before the request fires.
      // Edits made while the save is in-flight produce a new config object, so reading
      // this.config() on completion would mark those still-unsaved edits as saved.
      const sentFingerprint = this.configFingerprint(currentConfig);
      this.isSaving.set(true);
      return this.configProvider.save<ConfigModel, string>(currentConfig).pipe(
        map((response) => {
          if (typeof response === 'string') {
            this.savedConfigFingerprint.set(sentFingerprint);
            this.systemService.log('[ConfigService] Config saved to backend, response:', response);
            return true;
          }
          this.systemService.log(
            '[ConfigService] Unexpected response type when saving config to backend, expected string but got:',
            response
          );
          return false;
        }),
        catchError((error) => {
          this.systemService.log('[ConfigService] Failed to save config to backend', error);
          return of(false);
        }),
        finalize(() => this.isSaving.set(false))
      );
    });
  }

  /**
   * Exports the current config as a JSON file download
   * @returns true if export was successful, false if no config to export
   */
  exportConfig(): boolean {
    const currentConfig = this.config();
    if (!currentConfig) {
      this.systemService.log('[ConfigService] No config to export');
      return false;
    }

    const jsonString = JSON.stringify(currentConfig, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const fileName = `${currentConfig.appKey ?? 'default'}-config-export-${new Date().toISOString().split('T')[0]}.json`;

    this.fileService.downloadFile(blob, fileName);

    this.systemService.log('[ConfigService] Config exported successfully');
    return true;
  }

  /**
   * Imports a config from a JSON file and overwrites the current config
   * @param file The JSON file to import
   * @returns Promise that resolves when import is complete
   */
  async importConfig(file: File): Promise<void> {
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

    if (file.size > MAX_FILE_SIZE) {
      const error = new Error(`File size exceeds maximum allowed size of 10MB`);
      this.systemService.log('[ConfigService] File too large', { size: file.size, maxSize: MAX_FILE_SIZE });
      return Promise.reject(error);
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        try {
          const importedConfig = DataProvider.mapCasesReverse(JSON.parse(reader.result as string)) as Partial<ConfigModel>;
          // Exports made before the Phosphor icons carry FontAwesome names and styles
          convertFontAwesomeIcons(importedConfig);

          // Re-capitalize component keys (e.g. 'button' -> 'Button') since mapCasesReverse lowercases them
          if (importedConfig.components) {
            importedConfig.components = this.capitalizeComponentKeys(importedConfig.components);
            this.validateComponentTypes(importedConfig.components);
          }

          // Check matching appKey to alert user if they are trying to import a config from a different app (but still allow it)
          if (importedConfig.appKey && importedConfig.appKey !== this.envService.env.APP_KEY) {
            this.systemService.log(
              '[ConfigService] Warning: Imported config has a different appKey than the current app. This may lead to unexpected issues. Imported config:',
              importedConfig
            );
            const warnTitle = this.translateService.instant('config.importExport.import.appKeyMismatch.title');
            const warnMessage = this.translateService.instant('config.importExport.import.appKeyMismatch.message');
            this.toastService.showToast(warnTitle, warnMessage, 'warn');
            importedConfig.appKey = this.envService.env.APP_KEY; // Override appKey to prevent issues, but still allow import
          }

          // Merge imported config with DEFAULT_CONFIG to handle:
          // - New fields: use DEFAULT_CONFIG values
          // - Removed fields: ignore values from imported config
          const mergedConfig: ConfigModel = {
            ...DEFAULT_CONFIG,
            ...this.deepMergeConfigValues(DEFAULT_CONFIG, importedConfig),
            // Preserve components from imported config (not in DEFAULT_CONFIG so deepMerge skips it)
            components: importedConfig.components ?? this.config()?.components ?? DEFAULT_CONFIG.components,
            // Preserve the current appKey - it should never be overwritten by import
            appKey: this.config()?.appKey ?? this.envService.env.APP_KEY,
            // Always use the current config version
            version: CONFIG_VERSION
          };

          // Update the config signal
          this.config.set(mergedConfig);

          // Rebuild the form with the imported config
          this.buildForm();

          // Apply CSS variables (unsaved-changes state is derived from the config diff)
          this.applyTheme();

          this.systemService.log('[ConfigService] Config imported successfully', mergedConfig);
          resolve();
        } catch (error) {
          this.systemService.log('[ConfigService] Failed to parse imported config', error);
          reject(error);
        }
      };

      reader.onerror = () => {
        this.systemService.log('[ConfigService] Failed to read config file', reader.error);
        reject(reader.error);
      };

      reader.readAsText(file);
    });
  }

  /**
   * Deep merges the imported config values into the default config structure.
   * - Only keys that exist in defaultConfig are preserved (removes obsolete fields)
   * - Missing keys in importedConfig are filled with defaultConfig values (adds new fields)
   * @param defaultConfig The default configuration structure
   * @param importedConfig The imported configuration to merge
   * @returns The merged configuration as Partial<ConfigModel>
   */
  private deepMergeConfigValues(defaultConfig: Partial<ConfigModel>, importedConfig: Partial<ConfigModel>): Partial<ConfigModel> {
    const result: Partial<ConfigModel> = {};

    for (const key of Object.keys(defaultConfig) as Array<keyof ConfigModel>) {
      const defaultValue = defaultConfig[key];
      const importedValue = importedConfig[key];

      // If imported config doesn't have this key, use the default value
      if (importedValue === undefined) {
        (result as Record<string, unknown>)[key] = defaultValue;
        continue;
      }

      // If both values are objects (not arrays, not null), recursively merge
      if (
        defaultValue !== null &&
        importedValue !== null &&
        typeof defaultValue === 'object' &&
        typeof importedValue === 'object' &&
        !Array.isArray(defaultValue) &&
        !Array.isArray(importedValue)
      ) {
        (result as Record<string, unknown>)[key] = this.deepMergeObject(
          defaultValue as Record<string, unknown>,
          importedValue as Record<string, unknown>
        );
      } else {
        // For primitive values and arrays, use the imported value
        (result as Record<string, unknown>)[key] = importedValue;
      }
    }

    return result;
  }

  /**
   * Generic deep merge for nested objects
   * @param defaultObj The default object structure with all expected keys
   * @param importedObj The imported object that may contain partial or obsolete keys
   * @returns A merged object containing only keys from defaultObj with values from importedObj where available
   */
  private deepMergeObject(defaultObj: Record<string, unknown>, importedObj: Record<string, unknown>): Record<string, unknown> {
    const result: Record<string, unknown> = {};

    for (const key of Object.keys(defaultObj)) {
      const defaultValue = defaultObj[key];
      const importedValue = importedObj[key];

      if (importedValue === undefined) {
        result[key] = defaultValue;
        continue;
      }

      if (
        defaultValue !== null &&
        importedValue !== null &&
        typeof defaultValue === 'object' &&
        typeof importedValue === 'object' &&
        !Array.isArray(defaultValue) &&
        !Array.isArray(importedValue)
      ) {
        result[key] = this.deepMergeObject(defaultValue as Record<string, unknown>, importedValue as Record<string, unknown>);
      } else {
        result[key] = importedValue;
      }
    }

    // Preserve keys from importedObj that don't exist in defaultObj (e.g. responsive breakpoints like md, lg, xl in tagStyles)
    for (const key of Object.keys(importedObj)) {
      if (!(key in defaultObj)) {
        result[key] = importedObj[key];
      }
    }

    return result;
  }

  /**
   * Validates that each component's componentType matches its dictionary key.
   * Auto-corrects mismatches caused by a previous backend serialization bug
   * where unknown components fell back to componentType "Component".
   * @param components the components dictionary to validate
   */
  private validateComponentTypes(components: Record<PageComponentType, GsStructComponent<any>>): void {
    for (const [key, component] of Object.entries(components)) {
      if (!component?.componentType || component.componentType === key) continue;
      if (component.componentType === 'Component') {
        console.warn(
          `[ConfigService] ComponentType fallback detected: key="${key}" but componentType="${component.componentType}". ` +
            `This likely stems from a previous serialization bug. ` +
            `componentType will be corrected to "${key}".`
        );
        component.componentType = key as PageComponentType;
      } else {
        console.warn(
          `[ConfigService] ComponentType mismatch: key="${key}" but componentType="${component.componentType}". ` +
            `No automatic correction applied to avoid overwriting potentially valid data.`
        );
      }
    }
  }

  /**
   * Capitalizes the first letter of each component key (e.g. 'button' -> 'Button')
   * Component keys must be PascalCase to match PageComponentType
   */
  private capitalizeComponentKeys(
    components: Record<PageComponentType, GsStructComponent<any>>
  ): Record<PageComponentType, GsStructComponent<any>> {
    const result = {} as Record<PageComponentType, GsStructComponent<any>>;
    Object.keys(components).forEach((key) => {
      const newKey = key.charAt(0).toUpperCase() + key.slice(1);
      result[newKey as PageComponentType] = components[key as PageComponentType];
    });
    return result;
  }

  /**
   * Builds a stable, order-independent fingerprint of the config for unsaved-changes detection.
   * The volatile `updatedAt` field (bumped on every edit and on save) is excluded so that
   * programmatic re-writes with otherwise-identical values do not register as changes.
   * @param config the config to fingerprint
   * @returns a stable string fingerprint, or '' when no config is present
   */
  private configFingerprint(config: ConfigModel | undefined): string {
    if (!config) return '';
    return this.stableStringify(config, ['updatedAt']);
  }

  /**
   * Serializes a value to a string with object keys sorted recursively, so two structurally
   * equal objects always produce the same output regardless of key insertion order.
   * @param value the value to serialize
   * @param omitKeys top-level keys to exclude from the output
   * @returns the stable string representation
   */
  private stableStringify(value: unknown, omitKeys: string[] = []): string {
    if (value === undefined) return 'undefined';
    if (value === null || typeof value !== 'object') return JSON.stringify(value);
    if (Array.isArray(value)) return `[${value.map((entry) => this.stableStringify(entry)).join(',')}]`;
    const record = value as Record<string, unknown>;
    const entries = Object.keys(record)
      .filter((key) => !omitKeys.includes(key))
      .sort()
      .map((key) => `${JSON.stringify(key)}:${this.stableStringify(record[key])}`);
    return `{${entries.join(',')}}`;
  }
}
